/**
 * Hook của nhóm màn hỏi–đáp: luồng hỏi SSE (port `useAskTurns.ts` native) và dữ liệu đoạn chat của Hỏi đáp AI lẫn của
 * từng bot. View chỉ gọi hook ở đây, không gọi repository.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy, LoiHoi, moLuongHoi } from '../../../lib/repositories/tro-ly-ai.repository';
import { ghepLichSu, lichSuTrongPhien } from '../nghiep-vu/hoi';
import { NHIP_MS, soKyTuRut } from '../nghiep-vu/nha-chu';
import type { AskBody, AskResult, LuotLichSu, Refusal } from '../types';
import type { MauTieng, Step, StepStatus, Turn } from '../types-hoi';
import { KHOA, thuLai } from './useChung';

export interface TuyChonLuotHoi {
  /** Một mẩu tiếng — truyền NGUYÊN GÓI (`wav` hoặc `pcm`), để tệp này không phải biết đường tổng hợp nào đang chạy. */
  onTieng: (stt: number, goi: MauTieng) => void;
  /**
   * Máy chủ đã gửi xong — không còn mẩu tiếng nào. Hàng đợi phát mồi vài mẩu trước khi phát; thiếu tín hiệu này thì câu
   * trả lời chỉ có MỘT mẩu ngồi chờ mẩu thứ hai tới hết trần thời gian.
   */
  onXongTieng?: () => void;
  /**
   * Các lượt ĐÃ LƯU của đoạn chat đang mở, cũ nhất trước. Là HÀM: đọc lúc gửi, bên gọi không phải lo tham chiếu đổi.
   * ⚠️ Chỉ đưa vào lịch sử mà vai hiện tại được đọc (đoạn chat khoá theo vai ở tầng kho).
   */
  lichSuCu?: () => LuotLichSu[];
}

/**
 * Danh sách lượt hỏi, mỗi lượt đọc theo luồng sự kiện `POST /ask/stream`.
 *
 * Giữ CẢ DANH SÁCH chứ không chỉ lượt cuối, vì người ta hỏi tiếp ("còn tháng 3 thì sao"). Gửi `history` từ đây chứ không
 * để trợ lý tự nhớ: trợ lý không có phiên — đó là điều kiện để hai người hỏi cùng lúc không thấy hội thoại của nhau.
 */
export function useLuotHoi(tuyChon?: TuyChonLuotHoi) {
  // Trong ref: `hoi` là `useCallback` sống qua nhiều lần dựng, đóng gói tuỳ chọn vào nó là đóng gói bản của lần dựng
  // ĐẦU — âm thanh sẽ đi vào hàng đợi đã bị thay.
  const oTuyChon = useRef(tuyChon);
  useEffect(() => {
    oTuyChon.current = tuyChon;
  }, [tuyChon]);

  const [turns, setTurns] = useState<Turn[]>([]);
  const turnsRef = useRef<Turn[]>([]);
  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  const huy = useRef<AbortController | null>(null);

  const patch = useCallback((id: string, fn: (t: Turn) => Turn) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? fn(t) : t)));
  }, []);

  // ── CHỮ CHẢY ĐỀU, KHÔNG NHẢY CỤC ── (xem `nghiep-vu/nha-chu.ts`)
  const cho = useRef<{ id: string; con: string } | null>(null);
  const dongHo = useRef<ReturnType<typeof setInterval> | null>(null);

  const dungNhip = useCallback(() => {
    if (dongHo.current !== null) {
      clearInterval(dongHo.current);
      dongHo.current = null;
    }
  }, []);

  /** Đổ nốt phần còn chờ NGAY khi lượt kết thúc — dù xong, lỗi hay bấm Dừng; không thì người đọc mất mấy chữ cuối. */
  const xaHet = useCallback(() => {
    dungNhip();
    const c = cho.current;
    if (c?.con) {
      const ra = c.con;
      c.con = '';
      patch(c.id, (t) => ({ ...t, vanDang: (t.vanDang ?? '') + ra }));
    }
    cho.current = null;
  }, [patch, dungNhip]);

  const nap = useCallback(
    (id: string, them: string) => {
      if (cho.current?.id !== id) cho.current = { id, con: '' };
      cho.current.con += them;
      // "Giảm chuyển động" của hệ điều hành: chữ chạy dần là một hiệu ứng chuyển động — hiện thẳng.
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        xaHet();
        return;
      }
      if (dongHo.current !== null) return;
      dongHo.current = setInterval(() => {
        const c = cho.current;
        if (!c?.con) {
          dungNhip();
          return;
        }
        const so = soKyTuRut(c.con.length);
        const ra = c.con.slice(0, so);
        c.con = c.con.slice(so);
        patch(c.id, (t) => ({ ...t, vanDang: (t.vanDang ?? '') + ra }));
      }, NHIP_MS);
    },
    [patch, dungNhip, xaHet],
  );

  // Rời trang giữa lượt thì `setInterval` vẫn quay và gọi `setTurns` trên component đã tháo.
  useEffect(() => dungNhip, [dungNhip]);

  const hoi = useCallback(
    async (body: AskBody): Promise<AskResult | null> => {
      huy.current?.abort();
      const ctl = new AbortController();
      huy.current = ctl;

      const id = `turn_${Date.now()}`;
      let ketQua: AskResult | null = null;
      setTurns((prev) => [...prev, { id, question: body.question, userSql: body.sql, steps: [], running: true }]);

      const history = ghepLichSu(oTuyChon.current?.lichSuCu?.() ?? [], lichSuTrongPhien(turnsRef.current));

      try {
        for await (const ev of moLuongHoi({ ...body, history }, ctl.signal)) {
          if (ev.type === 'step') {
            // Chặng `running` được THAY bằng chính nó khi xong, không nối thêm dòng.
            patch(id, (t) => ({
              ...t,
              steps: [
                ...t.steps.filter((s) => !(s.step === ev.step && s.status === 'running')),
                {
                  step: String(ev.step ?? ''),
                  detail: String(ev.detail ?? ''),
                  status: (ev.status as StepStatus) ?? 'ok',
                  ...(Array.isArray(ev.oCot) ? { oCot: ev.oCot as Step['oCot'] } : {}),
                },
              ],
            }));
          } else if (ev.type === 'van') {
            // Vào bộ đệm chứ không vào thẳng state — chặng này có thể phát vài trăm mẩu.
            nap(id, String(ev.delta ?? ''));
          } else if (ev.type === 'tieng') {
            // ── KHÔNG ĐƯA ÂM THANH VÀO `turns` ── mỗi mẩu ~470 KB base64; để trong state là giữ vài chục MB
            // không ai thấy và vẽ lại cả danh sách cho thứ không hiện ra. Chuyển thẳng cho hàng đợi phát.
            oTuyChon.current?.onTieng(Number(ev.stt ?? 0), {
              wav: ev.wav as string | undefined,
              pcm: ev.pcm as string | undefined,
              tanSo: ev.tanSo as number | undefined,
              cau: ev.cau as number | undefined,
            });
          } else if (ev.type === 'van_thay') {
            // Máy chủ viết lại vì lời văn có số không nguồn. Dừng nhịp TRƯỚC khi thay, nếu không phần đuôi của bản
            // nháp SAI còn trong đệm sẽ nối vào sau bản đúng.
            dungNhip();
            cho.current = null;
            patch(id, (t) => ({ ...t, vanDang: String(ev.text ?? ''), daVietLai: true }));
          } else if (ev.type === 'done') {
            oTuyChon.current?.onXongTieng?.();
            const ket = ev.result as AskResult;
            ketQua = ket;
            patch(id, (t) => ({ ...t, result: ket, steps: (ket.steps as Step[] | undefined) ?? t.steps }));
          } else if (ev.type === 'suggestions') {
            // PHÁT SAU `done`: gợi ý tách ra một sự kiện riêng để câu trả lời hiện sớm hơn một lời gọi mô hình.
            patch(id, (t) =>
              t.result
                ? {
                    ...t,
                    result: {
                      ...t.result,
                      suggestions: ev.suggestions as string[] | undefined,
                      followUps: ev.followUps as string[] | undefined,
                    },
                  }
                : t,
            );
          } else if (ev.type === 'error') {
            patch(id, (t) => ({
              ...t,
              error: {
                code: String(ev.code ?? ''),
                message: String(ev.message ?? ''),
                followUps: ev.followUps as string[] | undefined,
                refusals: ev.refusals as Refusal[] | undefined,
                sql: ev.sql as string | undefined,
                nguon: ev.nguon as string | undefined,
              },
              steps: (ev.steps as Step[] | undefined) ?? t.steps,
            }));
          }
        }
      } catch (e) {
        if (!ctl.signal.aborted) {
          patch(id, (t) => ({
            ...t,
            error:
              e instanceof LoiHoi
                ? { code: e.code, message: e.message, refusals: e.refusals }
                : { code: 'NETWORK_ERROR', message: '' },
          }));
        }
      } finally {
        xaHet();
        // Báo hết gửi ở ĐÂY nữa: một lượt HỎNG GIỮA CHỪNG không bao giờ phát `done`, nên phần tiếng đã nhận sẽ nằm
        // chờ hết trần. Gọi hai lần vô hại.
        oTuyChon.current?.onXongTieng?.();
        patch(id, (t) => ({ ...t, running: false }));
      }
      return ketQua;
    },
    [patch, nap, dungNhip, xaHet],
  );

  const dung = useCallback(() => huy.current?.abort(), []);
  const xoaHet = useCallback(() => setTurns([]), []);

  // Rời màn giữa lượt: ngắt luồng để máy chủ dừng lượt gọi mô hình.
  useEffect(() => () => huy.current?.abort(), []);

  return { turns, hoi, dung, xoaHet, dangChay: turns.some((t) => t.running) };
}

/* ------------------------------------------------------------------ *
 * Gợi ý câu hỏi cho màn trống.
 * ------------------------------------------------------------------ */

/**
 * Câu mẫu SINH TỪ NGUỒN ĐANG CẮM, không phải danh sách viết sẵn — bốn câu cố định về thất thoát nước mời người dùng ERP
 * hỏi đúng câu không có dữ liệu. Ba nguồn theo độ tin cậy: câu đã CHỐT → câu mẫu có SQL chạy được → câu sinh từ lược
 * đồ (thuần quy tắc, rẻ). Lỗi KHÔNG hiện ra: gợi ý là tiện ích mở đầu, ô soạn vẫn dùng được.
 */
export function useGoiYCauHoi(sourceId: string, bat: boolean) {
  return useQuery({
    queryKey: [...KHOA.nguonChiTiet(sourceId), 'goi-y'],
    enabled: !!sourceId && bat,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const chot = await khoTroLy.cauHoi(sourceId);
      if (chot.length) return chot.map((q) => q.question).filter(Boolean);
      const mau = await khoTroLy.cauMau(sourceId);
      if (mau.length) return mau.map((s) => s.question).filter(Boolean);
      const sinh = await khoTroLy.sinhCauHoi(sourceId);
      return sinh.map((c) => c.question).filter(Boolean);
    },
  });
}

/* ------------------------------------------------------------------ *
 * Đoạn chat của Hỏi đáp AI.
 * ------------------------------------------------------------------ */

const khoaDoanTheoNguon = (khoaNguon: string) => [...KHOA.hoiThoai, 'theo-nguon', khoaNguon] as const;

/** Đoạn hỏi GIAO với tập nguồn đang chọn. `khoaNguon` sắp lại để {A,B} và {B,A} dùng chung một bản đệm. */
export function useHoiThoaiTheoNguon(nguon: string[]) {
  const khoaNguon = [...nguon].sort().join(',');
  return useQuery({
    queryKey: khoaDoanTheoNguon(khoaNguon),
    queryFn: () => khoTroLy.hoiThoai(nguon),
    enabled: nguon.length > 0,
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useMoHoiThoai(cvId: string | null) {
  return useQuery({
    queryKey: [...KHOA.hoiThoai, 'mo', cvId ?? ''],
    queryFn: () => khoTroLy.moHoiThoai(cvId ?? ''),
    enabled: !!cvId,
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useXoaHoiThoai() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => khoTroLy.xoaHoiThoai(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KHOA.hoiThoai }),
  });
}

/** Tạo đoạn và ghi lượt — gọi trong luồng gửi câu hỏi, nên trả hàm chứ không trả mutation. */
export function useGhiHoiThoai() {
  const qc = useQueryClient();
  const lamMoi = useCallback(() => void qc.invalidateQueries({ queryKey: [...KHOA.hoiThoai, 'theo-nguon'] }), [qc]);
  const taoDoan = useCallback(
    async (nguon: string[]) => {
      const cv = await khoTroLy.taoHoiThoai(nguon);
      lamMoi();
      return cv.id;
    },
    [lamMoi],
  );
  const ghiLuot = useCallback(
    async (cvId: string, cauHoi: string, traLoi: string, payload: Record<string, unknown>) => {
      const mUser = await khoTroLy.ghiLuot(cvId, { author: 'user', text: cauHoi });
      const mBot = await khoTroLy.ghiLuot(cvId, { author: 'bot', text: traLoi, payload });
      lamMoi();
      return [mUser.id, mBot.id] as const;
    },
    [lamMoi],
  );
  return { taoDoan, ghiLuot };
}

/* ------------------------------------------------------------------ *
 * Đoạn chat của một bot.
 * ------------------------------------------------------------------ */

const khoaDoanBot = (botId: string) => [...KHOA.bot, 'doan', botId] as const;

export function useDoanChatBot(botId: string, bat: boolean) {
  return useQuery({
    queryKey: khoaDoanBot(botId),
    queryFn: () => khoTroLy.doanChat(botId, 50),
    enabled: !!botId && bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useMoDoanBot(botId: string, cvId: string | null, bat: boolean) {
  return useQuery({
    queryKey: [...khoaDoanBot(botId), 'mo', cvId ?? ''],
    queryFn: () => khoTroLy.moDoan(botId, cvId ?? ''),
    enabled: !!botId && !!cvId && bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useXoaDoanBot(botId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cvId: string) => khoTroLy.xoaDoan(botId, cvId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: khoaDoanBot(botId) }),
  });
}

export function useGhiDoanBot(botId: string) {
  const qc = useQueryClient();
  const lamMoi = useCallback(() => void qc.invalidateQueries({ queryKey: khoaDoanBot(botId), exact: true }), [qc, botId]);
  const taoDoan = useCallback(async () => {
    const cv = await khoTroLy.taoDoan(botId);
    lamMoi();
    return cv.id;
  }, [botId, lamMoi]);
  const ghiLuot = useCallback(
    async (cvId: string, cauHoi: string, traLoi: string, payload: Record<string, unknown>) => {
      const mUser = await khoTroLy.themTinNhan(botId, cvId, { author: 'user', text: cauHoi });
      const mBot = await khoTroLy.themTinNhan(botId, cvId, { author: 'bot', text: traLoi, payload });
      lamMoi();
      return [mUser.id, mBot.id] as const;
    },
    [botId, lamMoi],
  );
  return { taoDoan, ghiLuot };
}
